import React from 'react';
import { Upload } from 'lucide-react';
import DataForSEOForm from './DataForSEOForm';

const LandingPage = ({
    isPro,
    setShowProModal,
    handleFileUpload,
    showPasteArea,
    setShowPasteArea,
    jsonInput,
    setJsonInput,
    handleJsonPaste,
    showDataForSEOForm,
    setShowDataForSEOForm,
    dataForSEOParams,
    setDataForSEOParams,
    handleDataForSEOSearch,
    loadingDataForSEO
}) => {
    return (
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <Upload className="mx-auto mb-4 text-blue-500" size={64} />
            <div className="flex items-center justify-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-gray-800">SERP Analyzer Pro</h1>
                {isPro && (
                    <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                        PRO
                    </span>
                )}
                {!isPro && (
                    <button
                        onClick={() => setShowProModal(true)}
                        className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1 rounded-full text-sm font-semibold transition-colors"
                    >
                        Sblocca PRO
                    </button>
                )}
            </div>
            <p className="text-gray-600 mb-8">Analisi SEO avanzata dei risultati di ricerca</p>

            <div className="flex flex-wrap gap-4 justify-center mb-8">
                <label className="cursor-pointer bg-blue-500 hover:bg-blue-600 text-white font-semibold px-6 py-3 rounded-lg transition-all active:scale-95 shadow-md">
                    Carica File JSON
                    <input
                        type="file"
                        accept=".json"
                        onChange={handleFileUpload}
                        className="hidden"
                    />
                </label>

                <button
                    onClick={() => {
                        setShowPasteArea(!showPasteArea);
                        setShowDataForSEOForm(false);
                    }}
                    className="bg-green-500 hover:bg-green-600 text-white font-semibold px-6 py-3 rounded-lg transition-all active:scale-95 shadow-md"
                >
                    {showPasteArea ? 'Nascondi Area' : 'Incolla JSON'}
                </button>

                {isPro && (
                    <button
                        onClick={() => {
                            setShowDataForSEOForm(!showDataForSEOForm);
                            setShowPasteArea(false);
                        }}
                        className="bg-purple-500 hover:bg-purple-600 text-white font-semibold px-6 py-3 rounded-lg transition-all active:scale-95 shadow-md"
                    >
                        {showDataForSEOForm ? 'Nascondi Form' : 'Analizza con DataForSEO'}
                    </button>
                )}
            </div>

            {showPasteArea && (
                <div className="mt-6 p-6 bg-gray-50 rounded-xl border border-gray-200">
                    <textarea
                        value={jsonInput}
                        onChange={(e) => setJsonInput(e.target.value)}
                        placeholder="Incolla qui il JSON di DataForSEO..."
                        className="w-full h-48 p-4 border rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                    <button
                        onClick={handleJsonPaste}
                        className="w-full mt-4 bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 rounded-lg transition-all"
                    >
                        Analizza JSON Incollato
                    </button>
                </div>
            )}

            {isPro && showDataForSEOForm && (
                <DataForSEOForm
                    dataForSEOParams={dataForSEOParams}
                    setDataForSEOParams={setDataForSEOParams}
                    handleDataForSEOSearch={handleDataForSEOSearch}
                    loadingDataForSEO={loadingDataForSEO}
                    isPro={isPro}
                />
            )}

            {/* Guide Section */}
            <div className="mt-12 text-left border-t pt-8">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Come utilizzare lo strumento</h3>
                <p className="text-gray-600 mb-4">
                    Questo tool è progettato per analizzare i dati JSON provenienti dalle API di <strong>DataForSEO</strong>.
                    Il formato supportato è l'<strong>Advanced SERP</strong> (Google &rarr; Organic &rarr; Live).
                </p>

                <div className="bg-blue-50 p-4 rounded-lg mb-6 border-l-4 border-blue-500">
                    <h4 className="font-bold text-blue-900 mb-2">Endpoint suggerito:</h4>
                    <code className="text-sm break-all">
                        POST https://api.dataforseo.com/v3/serp/google/organic/live/advanced
                    </code>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <h4 className="font-bold text-gray-700 mb-2 underline underline-offset-4 decoration-blue-500">Struttura JSON attesa:</h4>
                        <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto shadow-inner h-64 border border-gray-700">
                            {`{
  "tasks": [{
    "result": [{
      "keyword": "tua keyword",
      "items": [
        {
          "type": "organic",
          "rank_absolute": 1,
          "domain": "example.com",
          "title": "Titolo SEO",
          "url": "https://..."
        },
        { "type": "people_also_ask", "items": [...] },
        { "type": "popular_products", "items": [...] }
      ]
    }]
  }]
}`}
                        </pre>
                    </div>
                    <div>
                        <h4 className="font-bold text-gray-700 mb-2 underline underline-offset-4 decoration-green-500">Documentazione Ufficiale:</h4>
                        <p className="text-sm text-gray-600 mb-4">
                            Per maggiori dettagli sul formato e su come ottenere le API key, consulta la documentazione ufficiale di DataForSEO.
                        </p>
                        <a
                            href="https://dataforseo.com/apis/serp-api"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 bg-white border-2 border-green-500 text-green-600 font-bold px-4 py-2 rounded-lg hover:bg-green-50 transition-colors"
                        >
                            Google SERP API Docs &rarr;
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LandingPage;
